import { RegistryWorkspace, RegistryTableSurface } from '@workspace/silk-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useScope } from '@workspace/scope-context';
import {
  CommerceCatalogWorkspace,
  DescriptionText,
  Surface,
  VerticalStack,
  DataTable,
  DataTableToolbar,
  DataTableFacetedFilter,
  RegistryStatus,
  type ColumnDef,
  type ColumnFiltersState,
  type OnChangeFn,
  type SortingState,
} from '@workspace/silk-ui';
import { useNavigation } from '@/context/NavigationContext';
import { ChevronRight, Eye } from 'lucide-react';
import { lastPageIndexForTotal, toLimitOffset } from '@/lib/routePagination';
import { useCommerceRegistryQuery, withCommerceQueryValue } from '@/hooks/useCommerceRegistryQuery';
import { defineCommerceRegistryColumns } from '@/lib/commerceRegistryColumns';

const ORDER_SORT_FIELDS = ['customer', 'friendly_order_id', 'status', 'created_at', 'updated_at'] as const;
import {
  CommerceBreadcrumbHeader,
  buildCommerceSectionRoute,
  buildCommerceShellViewModel,
} from '@/views/commerce/catalogHierarchyShared';
import {
  approveCommissionProposal,
  addOrderNote,
  executeOrderAction,
  getCommerceErrorMessage,
  getCommerceErrorStatus,
  getOrder,
  listOrderActions,
  listOrderEvents,
  listOrderNotes,
  listOrders,
  rejectCommissionProposal,
  regenerateOrderConfirmation,
  resendOrderConfirmation,
  updateOrderStatus,
  type CommerceFacetBucket,
  type Order,
  type OrderAction,
  type OrderWorkspace,
  type TimelineEvent,
  type TimelineNote,
} from '@/lib/commerce';
import OrderView from './OrderView';
import { CommerceRegistrySearchField } from './CommerceRegistrySearchField';

function formatDate(value?: string | null): string {
  if (!value) {
    return 'Not available';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}

function orderReference(order: Pick<Order, 'friendly_order_id' | 'order_id'>): string {
  return order.friendly_order_id || order.order_id;
}

const secondaryButtonClass =
  'inline-flex items-center justify-center rounded-md border border-border/60 bg-background px-4 py-2 text-xs font-bold text-foreground transition-all hover:bg-muted/20 disabled:cursor-not-allowed disabled:opacity-50';

const ALL_STATUS_OPTIONS = ['active', 'cancelled', 'completed', 'failed', 'processing', 'submitted'];

function statusVariant(status?: string | null): 'default' | 'success' | 'warning' | 'error' | 'muted' {
  switch ((status || '').toLowerCase()) {
    case 'active':
    case 'completed':
      return 'success';
    case 'submitted':
    case 'processing':
      return 'warning';
    case 'cancelled':
    case 'failed':
      return 'error';
    default:
      return 'muted';
  }
}

function MobileOrderCard({
  order,
  onSelect,
}: {
  order: Order;
  onSelect: (orderId: string) => void;
}) {
  const customerName =
    [order.customer_snapshot?.first_name, order.customer_snapshot?.last_name]
      .filter(Boolean)
      .join(' ') || 'Unassigned customer';

  return (
    <button
      type="button"
      onClick={() => onSelect(order.order_id)}
      className="w-full px-4 py-4 text-left transition-colors hover:bg-muted/20 active:bg-muted/30"
      aria-label={`View order ${orderReference(order)} for ${customerName}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-black text-foreground truncate">{customerName}</p>
          <p className="mt-0.5 text-xs text-muted-foreground truncate">
            {String(order.customer_snapshot?.email || 'No email provided')}
          </p>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <RegistryStatus label={order.status} variant={statusVariant(order.status)} />
            <span className="font-mono text-[10px] font-bold text-muted-foreground/70 truncate">
              {orderReference(order)}
            </span>
          </div>
          <p className="mt-1.5 text-[10px] font-bold text-muted-foreground/60">
            {formatDate(order.updated_at)}
          </p>
        </div>
        <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground/40" />
      </div>
    </button>
  );
}

function focusedOrderIdFromPath(path: string | undefined): string {
  const source =
    typeof path === 'string' && path.trim()
      ? path
      : typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search}`
        : '';
  const search = source.includes('?') ? source.slice(source.indexOf('?')) : '';
  return new URLSearchParams(search).get('order_id')?.trim() ?? '';
}

function buildOrderListRoute(workspace: OrderWorkspace): string {
  return workspace === 'catalog-owner' ? '/commerce/orders' : '/reseller/orders';
}

function buildOrderQueryRoute(currentPath: string, workspace: OrderWorkspace, orderId: string | null): string {
  const queryIndex = currentPath.indexOf('?');
  const query = queryIndex >= 0 ? currentPath.slice(queryIndex) : '';
  return withCommerceQueryValue(`${buildOrderListRoute(workspace)}${query}`, 'order_id', orderId);
}

interface OrdersPageProps {
  workspace: OrderWorkspace;
}

export default function OrdersPage({ workspace }: OrdersPageProps) {
  const { activeTenant } = useScope();
  const navigation = useNavigation();
  const focusedOrderId = useMemo(() => focusedOrderIdFromPath((navigation as { path?: string }).path), [(navigation as { path?: string }).path]);
  const isResellerWorkspace = workspace === 'reseller';
  const [orders, setOrders] = useState<Order[]>([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [statusFacets, setStatusFacets] = useState<CommerceFacetBucket[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [notes, setNotes] = useState<TimelineNote[]>([]);
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [actions, setActions] = useState<OrderAction[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [submittingKey, setSubmittingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [accessDeniedStatus, setAccessDeniedStatus] = useState<number | null>(null);
  const query = useCommerceRegistryQuery({
    facetKeys: ['status'],
    facetValues: { status: ALL_STATUS_OPTIONS },
    sortFields: ORDER_SORT_FIELDS,
  });
  const requestSequence = useRef(0);
  const columnFilters = useMemo<ColumnFiltersState>(() =>
    query.facets.status.length > 0 ? [{ id: 'status', value: query.facets.status }] : [],
  [query.facets.status]);
  const sorting = useMemo<SortingState>(() => query.sort.map(token => ({
    id: token.startsWith('-') ? token.slice(1) : token,
    desc: token.startsWith('-'),
  })), [query.sort]);

  const selectedStatuses = useMemo(() => {
    return query.facets.status;
  }, [query.facets.status]);

  const loadOrderList = async () => {
    const requestID = ++requestSequence.current;
    setLoading(true);
    setError(null);
    setAccessDeniedStatus(null);
    try {
      const paginationQuery = toLimitOffset(query.pagination);
      const orderPage = await listOrders(workspace, {
        ...paginationQuery,
        ...(query.search ? { search: query.search } : {}),
        ...(selectedStatuses.length > 0 ? { status: selectedStatuses } : {}),
        ...(query.sort.length > 0 ? { sort: query.sort } : {}),
        include_facets: ['status'],
      });
      if (requestID !== requestSequence.current) return;
      const nextPageIndex = lastPageIndexForTotal(orderPage.total_count, query.pagination.pageSize);
      if (query.pagination.pageIndex > nextPageIndex) {
        query.onPaginationChange(current => ({ ...current, pageIndex: nextPageIndex }));
        return;
      }
      setOrders(orderPage.items);
      setTotalOrders(orderPage.total_count);
      setStatusFacets(orderPage.facets?.status ?? []);
      setSelectedOrderId(current => {
        if (focusedOrderId) {
          return focusedOrderId;
        }
        return current && orderPage.items.some(order => order.order_id === current) ? current : null;
      });
    } catch (err) {
      if (requestID !== requestSequence.current) return;
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
        setError(null);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to load orders.'));
      }
      setOrders([]);
      setTotalOrders(0);
      setSelectedOrderId(null);
    } finally {
      if (requestID === requestSequence.current) setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedOrderId(focusedOrderId || null);
  }, [focusedOrderId]);

  const loadOrderDetail = async (orderId: string) => {
    setDetailLoading(true);
    setError(null);
    try {
      const [order, nextEvents, nextNotes, nextActions] = await Promise.all([
        getOrder(workspace, orderId),
        listOrderEvents(workspace, orderId),
        isResellerWorkspace ? Promise.resolve([]) : listOrderNotes(workspace, orderId),
        isResellerWorkspace ? Promise.resolve([]) : listOrderActions(workspace, orderId),
      ]);
      setSelectedOrder(order);
      setNotes(nextNotes);
      setEvents(nextEvents);
      setActions(nextActions);
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
        setError(null);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to load order details.'));
      }
      setSelectedOrder(null);
      setNotes([]);
      setEvents([]);
      setActions([]);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (!activeTenant?.id) {
      setOrders([]);
      setSelectedOrderId(null);
      setSelectedOrder(null);
      setNotes([]);
      setEvents([]);
      setActions([]);
      setTotalOrders(0);
      setLoading(false);
      setDetailLoading(false);
      setError(null);
      setAccessDeniedStatus(null);
      return;
    }
    void loadOrderList();
  }, [activeTenant?.id, focusedOrderId, workspace, query.pagination.pageIndex, query.pagination.pageSize, query.search, query.sort.join('\u0000'), selectedStatuses.join('\u0000')]);

  useEffect(() => {
    if (!activeTenant?.id || !selectedOrderId) {
      setSelectedOrder(null);
      setNotes([]);
      setEvents([]);
      setActions([]);
      return;
    }
    void loadOrderDetail(selectedOrderId);
  }, [activeTenant?.id, selectedOrderId, workspace]);

  const refreshSelectedOrder = async () => {
    if (!selectedOrderId) {
      return;
    }
    await Promise.all([loadOrderList(), loadOrderDetail(selectedOrderId)]);
  };

  const handleStatusUpdate = async (status: string) => {
    if (!selectedOrderId || !status || status === selectedOrder?.status) {
      return;
    }
    setSubmittingKey('status');
    setError(null);
    setMessage(null);
    try {
      const updatedOrder = await updateOrderStatus(workspace, selectedOrderId, status);
      setSelectedOrder(updatedOrder);
      setOrders(current => current.map(item => (item.order_id === updatedOrder.order_id ? updatedOrder : item)));
      const [nextEvents, nextActions] = await Promise.all([
        listOrderEvents(workspace, selectedOrderId),
        listOrderActions(workspace, selectedOrderId),
      ]);
      setEvents(nextEvents);
      setActions(nextActions);
      setMessage(`Order status updated to ${updatedOrder.status}.`);
    } catch (err) {
      const statusErr = getCommerceErrorStatus(err);
      if (statusErr === 401 || statusErr === 403) {
        setAccessDeniedStatus(statusErr);
        setError(null);
        setMessage(null);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to update order status.'));
      }
    } finally {
      setSubmittingKey(null);
    }
  };

  const handleOrderAction = async (actionId: string) => {
    if (!selectedOrderId) {
      return;
    }
    setSubmittingKey(actionId);
    setError(null);
    setMessage(null);
    try {
      await executeOrderAction(workspace, selectedOrderId, actionId);
      await refreshSelectedOrder();
      setMessage(`Order action ${actionId} completed.`);
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
        setError(null);
        setMessage(null);
      } else {
        setError(getCommerceErrorMessage(err, `Failed to execute ${actionId}.`));
      }
    } finally {
      setSubmittingKey(null);
    }
  };

  const handleConfirmation = async (mode: 'resend' | 'regenerate') => {
    if (!selectedOrderId) {
      return;
    }
    setSubmittingKey(mode);
    setError(null);
    setMessage(null);
    try {
      if (mode === 'resend') {
        await resendOrderConfirmation(workspace, selectedOrderId);
      } else {
        await regenerateOrderConfirmation(workspace, selectedOrderId);
      }
      await refreshSelectedOrder();
      setMessage(`Order confirmation ${mode} completed.`);
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
        setError(null);
        setMessage(null);
      } else {
        setError(getCommerceErrorMessage(err, `Failed to ${mode} confirmation.`));
      }
    } finally {
      setSubmittingKey(null);
    }
  };

  const handleAddNote = async (noteBody: string) => {
    if (!selectedOrderId || !noteBody.trim()) {
      return;
    }
    setSubmittingKey('note');
    setError(null);
    setMessage(null);
    try {
      const note = await addOrderNote(workspace, selectedOrderId, noteBody.trim());
      setNotes(current => [note, ...(Array.isArray(current) ? current : [])]);
      try {
        const nextEvents = await listOrderEvents(workspace, selectedOrderId);
        setEvents(nextEvents);
      } catch {
        setEvents(current => [
          {
            event_id: `local-note-${note.note_id}`,
            event_type: 'order.note.added',
            actor_user_id: note.author_user_id ?? null,
            payload: { note_id: note.note_id },
            created_at: note.created_at,
          },
          ...(Array.isArray(current) ? current : []),
        ]);
      }
      setMessage('Order note added.');
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
        setError(null);
        setMessage(null);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to add note.'));
      }
    } finally {
      setSubmittingKey(null);
    }
  };

  const handleApproveCommissionProposal = async (comment?: string) => {
    if (!selectedOrder) {
      return;
    }
    setSubmittingKey('approve-commission');
    setError(null);
    setMessage(null);
    try {
      await approveCommissionProposal({
        catalog_owner_tenant_id: selectedOrder.catalog_owner_tenant_id,
        order_id: selectedOrder.order_id,
        commission_plan_id: selectedOrder.commission_plan_id,
        actor_type: 'user',
        source: 'app_portal',
        comment: comment || undefined,
      });
      await refreshSelectedOrder();
      setMessage('Commission proposal approved.');
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
        setError(null);
        setMessage(null);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to approve commission proposal.'));
      }
    } finally {
      setSubmittingKey(null);
    }
  };

  const handleRejectCommissionProposal = async (comment?: string) => {
    if (!selectedOrder) {
      return;
    }
    setSubmittingKey('reject-commission');
    setError(null);
    setMessage(null);
    try {
      const updatedOrder = await rejectCommissionProposal(selectedOrder.order_id, {
        catalog_owner_tenant_id: selectedOrder.catalog_owner_tenant_id,
        actor_type: 'user',
        source: 'app_portal',
        comment: comment || undefined,
      });
      setSelectedOrder(updatedOrder);
      setOrders(current => current.map(item => (item.order_id === updatedOrder.order_id ? updatedOrder : item)));
      const nextEvents = await listOrderEvents(workspace, selectedOrder.order_id);
      setEvents(nextEvents);
      setMessage('Commission proposal rejected.');
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
        setError(null);
        setMessage(null);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to reject commission proposal.'));
      }
    } finally {
      setSubmittingKey(null);
    }
  };

  const handleSelectOrder = useCallback(
    (orderId: string) => {
      setSelectedOrderId(orderId);
      navigation.navigate(buildOrderQueryRoute(navigation.path, workspace, orderId));
    },
    [navigation, workspace],
  );

  const handleCloseOrder = useCallback(() => {
    setSelectedOrderId(null);
    navigation.navigate(buildOrderQueryRoute(navigation.path, workspace, null), { replace: true });
  }, [navigation, workspace]);

  const handleSortingChange: OnChangeFn<SortingState> = updater => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    query.setSort(next.map(item => `${item.desc ? '-' : ''}${item.id}`));
  };

  const columns = useMemo<ColumnDef<Order>[]>(() => [
    {
      id: 'customer',
      accessorFn: order => [
        order.customer_snapshot?.first_name,
        order.customer_snapshot?.last_name,
        order.customer_snapshot?.email,
      ].filter(Boolean).join(' '),
      header: 'Customer',
      size: 250,
      cell: ({ row }) => {
        const order = row.original;
        const customerName =
          [order.customer_snapshot?.first_name, order.customer_snapshot?.last_name].filter(Boolean).join(' ') || 'Unassigned customer';
        return (
          <button
            type="button"
            onClick={() => handleSelectOrder(order.order_id)}
            className="group min-w-0 text-left"
            aria-label={`Inspect order ${orderReference(order)}`}
          >
            <p className="text-sm font-black text-foreground transition-colors group-hover:text-primary">
              {customerName}
            </p>
            <DescriptionText className="line-clamp-1 text-[11px]">
              {order.customer_snapshot?.email || 'No email provided'}
            </DescriptionText>
          </button>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 150,
      cell: ({ row }) => (
        <RegistryStatus label={row.original.status} variant={statusVariant(row.original.status)} />
      ),
      filterFn: (row, id, value) => value.includes(row.getValue(id)),
    },
    {
      id: 'friendly_order_id',
      accessorFn: order => orderReference(order),
      header: 'Reference',
      size: 250,
      cell: ({ row }) => (
        <span className="font-mono text-[11px] font-bold text-foreground/80">{orderReference(row.original)}</span>
      ),
    },
    {
      accessorKey: 'updated_at',
      header: 'Updated',
      size: 150,
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">{formatDate(row.original.updated_at)}</span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      size: 80,
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => (
        <div className="text-right">
          <button
            type="button"
            onClick={() => handleSelectOrder(row.original.order_id)}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
            aria-label={`View details for ${orderReference(row.original)}`}
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
        </div>
      ),
    },
  ], [handleSelectOrder]);
  defineCommerceRegistryColumns(columns, { mode: 'manual', sortFields: ORDER_SORT_FIELDS, registry: 'orders' });

  const shellViewModel = buildCommerceShellViewModel({
    activeTenant,
    accessDeniedStatus,
    activeSectionId: 'orders',
    searchQuery: query.search,
    searchPlaceholder: 'Search orders...',
    onSearchChange: query.setSearch,
    error,
    blockingReason: 'Your current role does not have permission to review orders.',
    onSelectSection: sectionId =>
      navigation.navigate(sectionId === 'orders' && workspace === 'reseller' ? '/reseller/orders' : buildCommerceSectionRoute(sectionId)),
    metrics: [
      { id: 'order-total', label: 'Total Orders', value: String(totalOrders) },
      { id: 'order-processing', label: 'Processing', value: String(orders.filter(o => o.status === 'processing').length) },
    ]
  });

  const workspaceTitle = workspace === 'catalog-owner' ? 'Commerce Orders' : 'Reseller Orders';
  const workspaceDescription =
    workspace === 'catalog-owner'
      ? 'Review all orders where the active tenant owns the catalog, including downstream reseller activity.'
      : 'Review reseller-owned orders, lifecycle state, and commission outcomes without owner-side fulfillment controls.';
  const hasTenant = Boolean(activeTenant?.id);

  return (
    <CommerceCatalogWorkspace
      viewModel={shellViewModel}
      padded={false}
      titleContent={
        <CommerceBreadcrumbHeader
          breadcrumbs={[{ label: 'Orders' }]}
          title={workspaceTitle}
          description={workspaceDescription}
        />
      }
      takeoverActive={!!selectedOrderId}
      replaceShellHeaderWhenTakeoverActive={true}
      takeoverContent={
        <OrderView
          order={selectedOrder}
          workspace={workspace}
          notes={notes}
          events={events}
          actions={actions}
          loading={detailLoading}
          onClose={handleCloseOrder}
          onStatusUpdate={handleStatusUpdate}
          onOrderAction={handleOrderAction}
          onConfirmation={handleConfirmation}
          onAddNote={handleAddNote}
          onApproveCommission={handleApproveCommissionProposal}
          onRejectCommission={handleRejectCommissionProposal}
          submittingKey={submittingKey}
          message={message}
        />
      }
    >
      <RegistryWorkspace>
        {hasTenant ? (
          <>
            {/* ── Desktop: full DataTable (md+) ─────────────────────────────── */}
            <div className="hidden md:flex md:flex-col flex-1 min-h-0">
              <RegistryTableSurface>
                <DataTable
                  mode="manual"
                  columns={columns}
                  data={orders}
                  sorting={sorting}
                  onSortingChange={handleSortingChange}
                  columnFilters={columnFilters}
                  onColumnFiltersChange={(updater) => {
                    const next = typeof updater === 'function' ? updater(columnFilters) : updater;
                    const statusFilter = next.find(entry => entry.id === 'status');
                    query.setFacet('status', Array.isArray(statusFilter?.value) ? statusFilter.value.map(String) : []);
                  }}
                  globalFilter={query.search}
                  pagination={query.pagination}
                  onPaginationChange={query.onPaginationChange}
                  manualPagination={true}
                  totalRowCount={totalOrders}
                  loading={loading}
                  className="border-none rounded-none"
                  paginationClassName="px-0 py-0"
                  showPagination={true}
                  emptyState={
                    <div className="px-0 py-12 text-center">
                      <p className="text-sm font-black text-foreground">No Orders Found</p>
                      <DescriptionText className="mt-1 text-xs">
                        {query.search || selectedStatuses.length > 0
                          ? 'No orders match the current search or filters.'
                          : 'There are no orders available.'}
                      </DescriptionText>
                    </div>
                  }
                  renderToolbar={(table) => (
                    <DataTableToolbar
                      table={table}
                      searchValue={query.search}
                      onSearchChange={query.setSearch}
                      searchPlaceholder="Search all orders..."
                      resultCount={totalOrders}
                      className="border-b border-border/40 bg-transparent px-0 py-0"
                      filters={
                        table.getColumn('status') && (
                          <DataTableFacetedFilter
                            title="Status"
                            selectedValues={new Set(table.getColumn('status')?.getFilterValue() as string[] ?? [])}
                            onFilterChange={(values) => table.getColumn('status')?.setFilterValue(Array.from(values))}
                            options={ALL_STATUS_OPTIONS.map(status => ({
                              label: statusFacets.find(bucket => bucket.value === status)?.label || status,
                              value: status,
                            }))}
                            counts={Object.fromEntries(statusFacets.map(bucket => [bucket.value, bucket.count]))}
                          />
                        )
                      }
                      bulkActions={
                        <button type="button" className={secondaryButtonClass} onClick={() => void loadOrderList()} disabled={loading || detailLoading}>
                          {loading ? 'Refreshing...' : 'Refresh Queue'}
                        </button>
                      }
                    />
                  )}
                />
              </RegistryTableSurface>
            </div>

            {/* ── Mobile: card list (< md) ──────────────────────────────────── */}
            <div className="md:hidden flex flex-col flex-1 min-h-0">
              <CommerceRegistrySearchField
                value={query.search}
                onChange={query.setSearch}
                placeholder="Search all orders..."
                resultCount={totalOrders}
              />
              {/* Mobile toolbar — status chip filters + refresh */}
              <div className="shrink-0 border-b border-border/40 bg-muted/5 px-4 py-2.5 flex items-center gap-2">
                <div className="flex-1 flex items-center gap-1.5 overflow-x-auto no-scrollbar min-w-0">
                  {ALL_STATUS_OPTIONS.map(status => {
                    const isActive = selectedStatuses.includes(status);
                    return (
                      <button
                        key={status}
                        type="button"
                        onClick={() => {
                          const next = new Set(selectedStatuses);
                          if (isActive) next.delete(status);
                          else next.add(status);
                          query.setFacet('status', Array.from(next));
                        }}
                        className={
                          isActive
                            ? 'shrink-0 inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-wide bg-primary text-primary-foreground transition-all'
                            : 'shrink-0 inline-flex items-center rounded-full border border-border/60 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground bg-background transition-all active:bg-muted/30'
                        }
                      >
                        {status}
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className={secondaryButtonClass}
                  onClick={() => void loadOrderList()}
                  disabled={loading || detailLoading}
                >
                  {loading ? 'Refreshing…' : 'Refresh'}
                </button>
              </div>

              {/* Card list */}
              <div className="flex-1 overflow-y-auto divide-y divide-border/40 overscroll-contain">
                {loading ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                  </div>
                ) : orders.length === 0 ? (
                  <div className="px-4 py-14 text-center">
                    <p className="text-sm font-black text-foreground">No Orders Found</p>
                    <DescriptionText className="mt-1 text-xs">
                      {query.search || selectedStatuses.length > 0
                        ? 'No orders matched the current search or filters.'
                        : 'There are no orders available.'}
                    </DescriptionText>
                  </div>
                ) : (
                  orders.map(order => (
                    <MobileOrderCard
                      key={order.order_id}
                      order={order}
                      onSelect={handleSelectOrder}
                    />
                  ))
                )}
              </div>

              {/* Mobile pagination */}
              <div className="shrink-0 border-t border-border/40 bg-muted/5 px-4 py-3 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => query.onPaginationChange(p => ({ ...p, pageIndex: Math.max(0, p.pageIndex - 1) }))}
                  disabled={query.pagination.pageIndex === 0 || loading}
                  className={secondaryButtonClass}
                >
                  Previous
                </button>
                <span className="text-xs font-bold text-muted-foreground tabular-nums">
                  {query.pagination.pageIndex + 1} / {lastPageIndexForTotal(totalOrders, query.pagination.pageSize) + 1}
                </span>
                <button
                  type="button"
                  onClick={() => query.onPaginationChange(p => ({ ...p, pageIndex: p.pageIndex + 1 }))}
                  disabled={query.pagination.pageIndex >= lastPageIndexForTotal(totalOrders, query.pagination.pageSize) || loading}
                  className={secondaryButtonClass}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        ) : (
          <Surface className="border-border/60 p-8">
            <VerticalStack gap={2} className="items-center text-center">
              <p className="text-sm font-black tracking-tight text-foreground">No tenant selected</p>
              <DescriptionText className="max-w-md text-sm">
                {isResellerWorkspace
                  ? 'Select an active tenant to review reseller order status, pricing snapshots, commissions, and fulfillment events.'
                  : 'Select an active tenant to review lifecycle state, confirmation actions, shared notes, and fulfillment events.'}
              </DescriptionText>
            </VerticalStack>
          </Surface>
        )}
      </RegistryWorkspace>
    </CommerceCatalogWorkspace>
  );
}
